// Recent samples, split by whether the model ranked the converter highly.
//
// A different question from the sample on the Signals page, which asks whether a
// lead's signals render correctly. This one asks how the model's ranking lined up
// with the outcome, so the cohort is the subject and the count belongs to it.
//
// The counts need no query: `funnelBreakdown` already returns conversions per
// segment. They sit on the cohort buttons rather than in a sentence, because two
// cohorts that partition the conversions make any statement about one trivially
// true of the other, and a zero reads as a finding when it is the absence of one.
//
// The rows are the same card the Signals sample uses, so the signals a lead
// carries are readable here too. A missed conversion is only diagnosable if you
// can see what the model saw.
import { inject, observer } from 'mobx-react';
import React from 'react';

import { CustomerFitSampleLead } from '../../../models/SampleLead';
import Store from '../../../store';
import { Threshold } from '../../../models/ModelPerformances';
import Spinner from '../Spinner';
import LeadProfileCard from './LeadProfileCard';
import { FunnelSegment } from './PerformanceBlocks';
import '../trees/segmented.css';
import ModelBaseNames from '../../../models/modelBase/ModelBaseNames';
import './performanceSamples.css';

type Cohort = 'all' | 'fn' | 'tp';

/** The tiers a lead can land in, split by whether the model ranked it highly. */
const MISSED: Threshold[] = ['low', 'medium'];
const CAUGHT: Threshold[] = ['very good', 'good'];

/** Cards are tall, so a handful is a sample and a page of them is a list. */
const LIMIT = 5;

const count = (value: number) => Math.round(value).toLocaleString('en-US');

function PerformanceSamples({
  store,
  funnel,
}: {
  store?: Store;
  funnel: Record<string, FunnelSegment> | null;
}) {
  const [cohort, setCohort] = React.useState<Cohort>('fn');
  const { spotCheckPage } = store;

  const load = React.useCallback(
    (next: Cohort) => {
      setCohort(next);
      // Kept, not redrawn: each cohort has its own sample now, so coming back
      // to this page or to a cohort shows the leads it showed last time. A new
      // hundred is the Review page's Refresh.
      store
        .getSpotCheckLeads(next === 'fn', next === 'tp', true)
        .catch(() => undefined);
    },
    [store]
  );

  // Fetched on mount for the cohort this panel opens on, which is the failure
  // case: a page about performance should open on where the model is wrong.
  React.useEffect(() => {
    load('fn');
  }, [load]);

  const conversionsIn = (segments: Threshold[]) =>
    segments.reduce(
      (sum, segment) => sum + (Number(funnel?.[segment]?.conversions) || 0),
      0
    );
  const missed = conversionsIn(MISSED);
  const caught = conversionsIn(CAUGHT);
  const total = missed + caught;

  const leads = (spotCheckPage.leadsSpotCheck ?? []) as CustomerFitSampleLead[];

  return (
    <div className="psmp">
      <div className="psmp-head">
        <h5 className="psmp-title">
          <i className="fas fa-users psmp-icon" aria-hidden="true" />
          Recent samples
        </h5>
        {/* The count sits on the cohort rather than in a sentence below it. Two
            cohorts that partition the conversions make any single statement
            trivially true of the other: "0% landed in Low or Medium" and "100%
            were ranked Very Good or Good" are one fact said twice, and the
            zero case reads as a finding when it is the absence of one. On the
            button it is information before you click. */}
        <div className="seg" role="group" aria-label="Cohort">
          <button
            type="button"
            className={`seg-item ${cohort === 'fn' ? 'seg-item--on' : ''}`}
            aria-pressed={cohort === 'fn'}
            onClick={() => load('fn')}
          >
            Missed conversions{' '}
            {total > 0 && <span className="psmp-count">{count(missed)}</span>}
          </button>
          <button
            type="button"
            className={`seg-item ${cohort === 'tp' ? 'seg-item--on' : ''}`}
            aria-pressed={cohort === 'tp'}
            onClick={() => load('tp')}
          >
            Ranked highly{' '}
            {total > 0 && <span className="psmp-count">{count(caught)}</span>}
          </button>
          <button
            type="button"
            className={`seg-item ${cohort === 'all' ? 'seg-item--on' : ''}`}
            aria-pressed={cohort === 'all'}
            onClick={() => load('all')}
          >
            All leads
          </button>
        </div>
      </div>

      {spotCheckPage.loadingSpotCheck ? (
        <Spinner />
      ) : (
        <>
          {/* Empty is a real answer, and on the missed cohort it is the good
              one. Said plainly rather than left as a blank panel. */}
          {!leads.length && (
            <p className="psmp-empty">
              {cohort === 'fn'
                ? 'No conversions landed in Low or Medium in this dataset.'
                : 'Nothing in this cohort.'}
            </p>
          )}
          {/* The same card as the sample on Review, Signals, so the signals a
              lead carries are readable here too. That is most of what makes a
              missed conversion diagnosable rather than just countable. */}
          {/* Only when there is something in it: an empty tinted panel reads as
              a thing that failed to load rather than a cohort with no members. */}
          {leads.length > 0 && (
            <div className="psmp-cards">
              {leads.slice(0, LIMIT).map((lead) => (
                <LeadProfileCard
                  key={lead.email}
                  lead={lead}
                  tenant={store.tenant}
                  modelId={store.activeModel.modelId}
                  showTrees={store.activeModel.base === ModelBaseNames.trees}
                />
              ))}
            </div>
          )}
          {leads.length > LIMIT && (
            <p className="psmp-more">
              {LIMIT} of {count(leads.length)} sampled. The full sample is on
              Review, Signals.
            </p>
          )}
        </>
      )}
    </div>
  );
}

export default inject('store')(observer(PerformanceSamples));
